/* ==========================================================================
   ACG Car Show Finder
   Static dataset + client-side filtering. Leaflet is progressive enhancement:
   if it fails to load the list still works on its own.
   ========================================================================== */
(function () {
  'use strict';

  var PAGE = 60; // cards rendered per chunk

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function parseDate(s) {
    if (!s) return null;
    var p = s.split('-');
    if (p.length !== 3) return null;
    var d = new Date(+p[0], +p[1] - 1, +p[2]);
    return isNaN(d) ? null : d;
  }

  function startOfToday() {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function haversine(lat1, lng1, lat2, lng2) {
    var R = 3958.8;
    var dLat = ((lat2 - lat1) * Math.PI) / 180;
    var dLng = ((lng2 - lng1) * Math.PI) / 180;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var MONTHS_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  var ICON = {
    pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z"/><circle cx="12" cy="10" r="3"/></svg>',
    link: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><path d="M15 3h6v6"/><path d="M10 14L21 3"/></svg>'
  };

  function init(root) {
    var src = root.dataset.src;
    if (!src) return;

    var els = {
      list: root.querySelector('[data-shows-list]'),
      count: root.querySelector('[data-shows-count]'),
      scope: root.querySelector('[data-shows-scope]'),
      search: root.querySelector('[data-shows-search]'),
      stateSel: root.querySelector('[data-shows-state]'),
      dateSel: root.querySelector('[data-shows-date]'),
      near: root.querySelector('[data-shows-near]'),
      clear: root.querySelector('[data-shows-clear]'),
      mapEl: root.querySelector('[data-shows-map]'),
      mapArea: root.querySelector('[data-shows-maparea]'),
      typeSel: root.querySelector('[data-shows-type]'),
      switchBtns: Array.prototype.slice.call(root.querySelectorAll('[data-shows-view]')),
      statTotal: root.querySelector('[data-stat-total]'),
      statStates: root.querySelector('[data-stat-states]')
    };

    var DATA = null;
    var shows = [];
    var filtered = [];
    var rendered = 0;
    var selected = null;
    var origin = null; // {lat,lng} from "near me"

    var state = {
      q: '',
      type: '',
      st: '',
      date: 'upcoming',
      bounds: false // "search this area" — off by default, unlike TRC's always-on bounds filter
    };

    var map = null;
    var cluster = null;
    var wasNarrowed = null;
    var markers = {};

    /* ── Data ─────────────────────────────────────────────── */
    function load() {
      fetch(src)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (d) {
          DATA = d;
          shows = d.shows.map(function (row, i) {
            var dt = parseDate(row[4]);
            return {
              i: i,
              name: row[0],
              type: row[1],
              typeLabel: d.type_labels[row[1]],
              state: d.states[row[2]],
              city: row[3],
              start: row[4],
              end: row[5],
              street: row[6],
              lat: row[7] || null,
              lng: row[8] || null,
              url: row[9],
              date: dt,
              ts: dt ? dt.getTime() : Infinity,
              hay: (row[0] + ' ' + row[3] + ' ' + d.states[row[2]]).toLowerCase()
            };
          });
          buildStates();
          buildStats();
          initMap();
          readParams();
          apply();
        })
        .catch(function (e) {
          if (window.console) console.error('[acg-car-shows]', e);
          els.list.innerHTML =
            '<div class="acg-shows__empty"><h3>Show list unavailable</h3><p>We could not load the show schedule. Please refresh the page.</p></div>';
        });
    }

    function buildStates() {
      var counts = {};
      shows.forEach(function (s) {
        counts[s.state] = (counts[s.state] || 0) + 1;
      });
      DATA.states.forEach(function (st) {
        var o = document.createElement('option');
        o.value = st;
        o.textContent = st + ' (' + (counts[st] || 0) + ')';
        els.stateSel.appendChild(o);
      });
    }

    function buildStats() {
      if (els.statTotal) els.statTotal.textContent = shows.length.toLocaleString();
      if (els.statStates) els.statStates.textContent = DATA.states.length;
    }

    /* ── Filtering ────────────────────────────────────────── */
    function dateWindow() {
      // Every window starts at today and there is no "any date" escape hatch:
      // the dataset is static, so this is what keeps yesterday's shows off the
      // page as it ages between refreshes.
      var today = startOfToday();
      var end = null;
      if (state.date === 'weekend') {
        // Next Fri 00:00 → Sun 23:59 (today onward if we're already in it)
        var day = today.getDay();
        var toFri = (5 - day + 7) % 7;
        var from = new Date(today);
        from.setDate(from.getDate() + toFri);
        if (day === 6 || day === 0) from = today; // already the weekend
        end = new Date(from);
        end.setDate(end.getDate() + (from.getDay() === 6 ? 1 : from.getDay() === 0 ? 0 : 2));
        return { from: from, to: end };
      }
      if (state.date === 'month') {
        end = new Date(today);
        end.setMonth(end.getMonth() + 1);
      } else if (state.date === '3months') {
        end = new Date(today);
        end.setMonth(end.getMonth() + 3);
      }
      return { from: today, to: end }; // 'upcoming' → to === null
    }

    // A multi-day show counts as live until its end date. Long spans in the
    // source data are recurrence markers, not real runs, so they're ignored.
    function inDateWindow(s, win) {
      if (!s.date) return false;
      var tail = s.date;
      if (s.end) {
        var e = parseDate(s.end);
        if (e && (e - s.date) / 864e5 <= 14) tail = e;
      }
      if (tail < win.from) return false;
      if (win.to && s.date > win.to) return false;
      return true;
    }

    function apply() {
      var win = dateWindow();
      var q = state.q.trim().toLowerCase();
      var b = state.bounds && map ? map.getBounds() : null;

      filtered = shows.filter(function (s) {
        if (state.type !== '' && s.type !== +state.type) return false;
        if (state.st && s.state !== state.st) return false;
        if (q && s.hay.indexOf(q) === -1) return false;
        if (win && !inDateWindow(s, win)) return false;
        if (b) {
          if (!s.lat) return false;
          if (!b.contains([s.lat, s.lng])) return false;
        }
        return true;
      });

      if (origin) {
        filtered.forEach(function (s) {
          s.dist = s.lat ? haversine(origin.lat, origin.lng, s.lat, s.lng) : null;
        });
        filtered.sort(function (a, b2) {
          return (a.dist == null ? 1e9 : a.dist) - (b2.dist == null ? 1e9 : b2.dist);
        });
      } else {
        filtered.sort(function (a, b2) {
          return a.ts - b2.ts || a.name.localeCompare(b2.name);
        });
      }

      renderTypeCounts();
      renderCount();
      renderList(true);
      renderMarkers();
      syncClear();
      writeParams();
    }

    /* ── Rendering ────────────────────────────────────────── */
    // Each option shows how many results it would yield under the *other* active
    // filters — so "Concours (3)" means 3 in this state/date, not 95 nationwide.
    function renderTypeCounts() {
      var win = dateWindow();
      var q = state.q.trim().toLowerCase();
      var b = state.bounds && map ? map.getBounds() : null;
      var tally = [0, 0, 0, 0];
      var total = 0;

      shows.forEach(function (s) {
        if (state.st && s.state !== state.st) return;
        if (q && s.hay.indexOf(q) === -1) return;
        if (win && !inDateWindow(s, win)) return;
        if (b && (!s.lat || !b.contains([s.lat, s.lng]))) return;
        tally[s.type]++;
        total++;
      });

      var labels = ['All types'].concat(DATA.type_labels);
      for (var i = 0; i < els.typeSel.options.length; i++) {
        var opt = els.typeSel.options[i];
        var n = opt.value === '' ? total : tally[+opt.value];
        opt.textContent = labels[i] + ' (' + n.toLocaleString() + ')';
      }
    }

    function renderCount() {
      els.count.innerHTML = '<em>' + filtered.length.toLocaleString() + '</em> ' + (filtered.length === 1 ? 'show' : 'shows');
      var bits = [];
      if (state.st) bits.push(state.st);
      if (state.type !== '') bits.push(DATA.type_labels[+state.type]);
      if (origin) bits.push('nearest first');
      els.scope.textContent = bits.join(' · ');
    }

    function cardHTML(s) {
      var d = s.date;
      var badge = d
        ? '<div class="acg-show__m">' + MONTHS[d.getMonth()] + '</div>' +
          '<div class="acg-show__d">' + d.getDate() + '</div>' +
          '<div class="acg-show__wd">' + DAYS[d.getDay()] + '</div>'
        : '<div class="acg-show__m">Date</div><div class="acg-show__d">—</div><div class="acg-show__wd">TBA</div>';

      var dist = s.dist != null ? '<span class="acg-show__dist">' + Math.round(s.dist) + ' mi</span>' : '';
      var where = esc(s.city) + ', ' + esc(s.state) + (s.street ? '<br>' + esc(s.street) : '');

      var links = '';
      if (s.lat) {
        links +=
          '<a class="acg-show__link acg-show__link--primary" target="_blank" rel="noopener" ' +
          'href="https://www.google.com/maps/dir/?api=1&destination=' + s.lat + ',' + s.lng + '">' +
          ICON.pin + 'Directions</a>';
      }
      if (s.url) {
        links +=
          '<a class="acg-show__link acg-show__link--ghost" target="_blank" rel="noopener nofollow" href="' +
          esc(s.url) + '">' + ICON.link + 'Event site</a>';
      }

      return (
        '<div class="acg-show__date">' + badge + '</div>' +
        '<div class="acg-show__body">' +
        '<div class="acg-show__meta">' +
        '<span class="acg-show__type" data-t="' + s.type + '">' + esc(s.typeLabel) + '</span>' + dist +
        '</div>' +
        '<div class="acg-show__name">' + esc(s.name) + '</div>' +
        '<div class="acg-show__loc">' + where + '</div>' +
        (links ? '<div class="acg-show__links">' + links + '</div>' : '') +
        '</div>'
      );
    }

    function renderList(reset) {
      if (reset) {
        els.list.innerHTML = '';
        rendered = 0;
        if (!filtered.length) {
          els.list.innerHTML =
            '<div class="acg-shows__empty"><h3>No shows match</h3><p>Try a wider date range, or clear a filter.</p>' +
            '<button type="button" class="acg-show__link acg-show__link--primary" data-shows-reset>Reset filters</button></div>';
          return;
        }
      }

      var frag = document.createDocumentFragment();
      var lastMonth = rendered > 0 ? monthKey(filtered[rendered - 1]) : null;
      var stop = Math.min(rendered + PAGE, filtered.length);

      for (var i = rendered; i < stop; i++) {
        var s = filtered[i];
        if (!origin) {
          var mk = monthKey(s);
          if (mk !== lastMonth) {
            var h = document.createElement('div');
            h.className = 'acg-shows__month';
            h.textContent = mk;
            frag.appendChild(h);
            lastMonth = mk;
          }
        }
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'acg-show';
        card.dataset.i = s.i;
        card.innerHTML = cardHTML(s);
        if (selected === s.i) card.setAttribute('aria-current', 'true');
        frag.appendChild(card);
      }
      rendered = stop;

      var old = els.list.querySelector('.acg-shows__more');
      if (old) old.remove();
      els.list.appendChild(frag);

      if (rendered < filtered.length) {
        var sentinel = document.createElement('div');
        sentinel.className = 'acg-shows__more';
        els.list.appendChild(sentinel);
        observer.observe(sentinel);
      }
    }

    function monthKey(s) {
      return s.date ? MONTHS_LONG[s.date.getMonth()] + ' ' + s.date.getFullYear() : 'Date to be announced';
    }

    // The rail is its own scroll container, so that — not the viewport — is the
    // observer root; with the viewport as root the sentinel never comes into view.
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            observer.unobserve(e.target);
            renderList(false);
          }
        });
      },
      { root: els.list, rootMargin: '800px' }
    );

    /* ── Map ──────────────────────────────────────────────── */
    function initMap() {
      if (typeof L === 'undefined' || !els.mapEl) {
        if (els.mapEl) els.mapEl.parentNode.style.display = 'none';
        root.style.setProperty('--shows-rail-w', '100%');
        return;
      }

      map = L.map(els.mapEl, { zoomControl: true, scrollWheelZoom: true }).setView([39.5, -98.35], 4);
      L.tileLayer(els.mapEl.dataset.tiles || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: els.mapEl.dataset.attribution || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map);

      if (L.markerClusterGroup) {
        cluster = L.markerClusterGroup({
          maxClusterRadius: 55,
          showCoverageOnHover: false,
          iconCreateFunction: function (c) {
            var n = c.getChildCount();
            var sz = n < 10 ? 34 : n < 100 ? 40 : 48;
            return L.divIcon({
              html: '<div class="acg-shows__cluster" style="width:' + sz + 'px;height:' + sz + 'px;font-size:' + (sz < 40 ? 12 : 14) + 'px">' + n + '</div>',
              className: '',
              iconSize: [sz, sz]
            });
          }
        });
        map.addLayer(cluster);
      } else {
        cluster = L.layerGroup().addTo(map);
      }

      map.on('moveend', function () {
        if (state.bounds) apply();
      });

      if (els.mapArea) {
        els.mapArea.addEventListener('click', function () {
          state.bounds = !state.bounds;
          els.mapArea.setAttribute('aria-pressed', String(state.bounds));
          apply();
        });
      }
    }

    function pinIcon(on) {
      return L.divIcon({
        className: '',
        html: '<div class="acg-shows__pin' + (on ? ' acg-shows__pin--on' : '') + '"></div>',
        iconSize: on ? [30, 30] : [20, 20],
        iconAnchor: on ? [15, 30] : [10, 20]
      });
    }

    function renderMarkers() {
      if (!map) return;
      cluster.clearLayers();
      markers = {};
      var pts = [];

      filtered.forEach(function (s) {
        if (!s.lat) return;
        var m = L.marker([s.lat, s.lng], { icon: pinIcon(selected === s.i) });
        m.bindPopup(
          '<div class="acg-shows__pop-name">' + esc(s.name) + '</div>' +
          '<div class="acg-shows__pop-meta">' + esc(s.city) + ', ' + esc(s.state) +
          (s.date ? ' &middot; ' + MONTHS[s.date.getMonth()] + ' ' + s.date.getDate() : '') + '</div>' +
          '<a class="acg-shows__pop-link" target="_blank" rel="noopener" ' +
          'href="https://www.google.com/maps/dir/?api=1&destination=' + s.lat + ',' + s.lng + '">Get directions</a>'
        );
        m.on('click', function () {
          select(s.i, false);
        });
        markers[s.i] = m;
        cluster.addLayer(m);
        pts.push([s.lat, s.lng]);
      });

      // Fit only when a filter has actually narrowed things down. On the full
      // nationwide list, AK and HI drag the bounds out into open ocean, so the
      // continental view reads far better than a true fit.
      if (state.bounds) return; // user is driving the map — never fight them
      var narrowed = !!(state.st || state.q || origin);
      if (narrowed) {
        if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 11, animate: false });
      } else if (wasNarrowed !== false) {
        // first paint, or the visitor just cleared a location filter — go back
        // to the national view instead of stranding them on the last zoom
        map.setView([39.5, -96.5], 4, { animate: false });
      }
      wasNarrowed = narrowed;
    }

    function select(i, fromCard) {
      var prev = selected;
      selected = i;

      root.querySelectorAll('.acg-show[aria-current]').forEach(function (el) {
        el.removeAttribute('aria-current');
      });
      var card = root.querySelector('.acg-show[data-i="' + i + '"]');
      if (card) {
        card.setAttribute('aria-current', 'true');
        if (!fromCard) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      if (map) {
        if (prev != null && markers[prev]) markers[prev].setIcon(pinIcon(false));
        if (markers[i]) {
          markers[i].setIcon(pinIcon(true));
          if (fromCard) {
            var s = shows[i];
            map.setView([s.lat, s.lng], Math.max(map.getZoom(), 10), { animate: true });
            var mk = markers[i];
            setTimeout(function () {
              if (cluster.zoomToShowLayer) cluster.zoomToShowLayer(mk, function () { mk.openPopup(); });
              else mk.openPopup();
            }, 250);
          }
        }
      }
    }

    /* ── URL state ────────────────────────────────────────── */
    function writeParams() {
      if (!window.history || !history.replaceState) return;
      var p = new URLSearchParams();
      if (state.q) p.set('q', state.q);
      if (state.type !== '') p.set('type', DATA.types[+state.type]);
      if (state.st) p.set('state', state.st);
      if (state.date !== 'upcoming') p.set('when', state.date);
      var qs = p.toString();
      history.replaceState(null, '', qs ? '?' + qs : location.pathname);
    }

    function readParams() {
      var p = new URLSearchParams(location.search);
      if (p.get('q')) {
        state.q = p.get('q');
        els.search.value = state.q;
      }
      var t = p.get('type');
      if (t && DATA.types.indexOf(t) > -1) {
        state.type = String(DATA.types.indexOf(t));
        els.typeSel.value = state.type;
        els.typeSel.classList.add('acg-shows__select--on');
      }
      var st = p.get('state');
      if (st && DATA.states.indexOf(st) > -1) {
        state.st = st;
        els.stateSel.value = st;
        els.stateSel.classList.add('acg-shows__select--on');
      }
      var w = p.get('when');
      if (w && ['upcoming', 'weekend', 'month', '3months'].indexOf(w) > -1) {
        state.date = w;
        els.dateSel.value = w;
      }
    }

    function syncClear() {
      var dirty = state.q || state.type !== '' || state.st || state.date !== 'upcoming' || origin || state.bounds;
      els.clear.hidden = !dirty;
    }

    function resetAll() {
      state = { q: '', type: '', st: '', date: 'upcoming', bounds: false };
      origin = null;
      selected = null;
      els.search.value = '';
      els.stateSel.value = '';
      els.stateSel.classList.remove('acg-shows__select--on');
      els.dateSel.value = 'upcoming';
      els.near.setAttribute('aria-pressed', 'false');
      els.near.querySelector('span').textContent = 'Near me';
      if (els.mapArea) els.mapArea.setAttribute('aria-pressed', 'false');
      els.typeSel.value = '';
      els.typeSel.classList.remove('acg-shows__select--on');
      apply();
    }

    /* ── Events ───────────────────────────────────────────── */
    var debounce;
    els.search.addEventListener('input', function () {
      clearTimeout(debounce);
      var v = this.value;
      debounce = setTimeout(function () {
        state.q = v;
        apply();
      }, 220);
    });

    els.typeSel.addEventListener('change', function () {
      state.type = this.value;
      this.classList.toggle('acg-shows__select--on', this.value !== '');
      apply();
    });

    els.stateSel.addEventListener('change', function () {
      state.st = this.value;
      this.classList.toggle('acg-shows__select--on', !!this.value);
      apply();
    });

    els.dateSel.addEventListener('change', function () {
      state.date = this.value;
      apply();
    });

    els.near.addEventListener('click', function () {
      var btn = this;
      var label = btn.querySelector('span');
      if (origin) {
        origin = null;
        btn.setAttribute('aria-pressed', 'false');
        label.textContent = 'Near me';
        apply();
        return;
      }
      if (!navigator.geolocation) return;
      label.textContent = 'Locating…';
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          btn.setAttribute('aria-pressed', 'true');
          label.textContent = 'Nearest first';
          if (map) map.setView([origin.lat, origin.lng], 8);
          apply();
        },
        function () {
          label.textContent = 'Near me';
        },
        { timeout: 10000 }
      );
    });

    els.clear.addEventListener('click', resetAll);

    els.list.addEventListener('click', function (e) {
      if (e.target.closest('[data-shows-reset]')) {
        resetAll();
        return;
      }
      if (e.target.closest('a')) return; // let Directions / Event site through
      var card = e.target.closest('.acg-show');
      if (card) select(+card.dataset.i, true);
    });

    els.switchBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        root.dataset.view = btn.dataset.showsView;
        els.switchBtns.forEach(function (b) {
          b.setAttribute('aria-pressed', String(b === btn));
        });
        if (map && btn.dataset.showsView === 'map') setTimeout(function () { map.invalidateSize(); }, 60);
      });
    });

    // The theme's header is sticky at z-index 999, so this section's own sticky
    // bar has to start below it. Measure it rather than hard-coding a height:
    // the header differs between breakpoints and can change with theme settings.
    function syncHeaderOffset() {
      var h = 0;
      var nodes = document.querySelectorAll('.shopify-section-group-header-group, sticky-header');
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (root.contains(el)) continue;
        var cs = window.getComputedStyle(el);
        if ((cs.position === 'sticky' || cs.position === 'fixed') && parseFloat(cs.top || '0') === 0) {
          h = Math.max(h, el.offsetHeight);
        }
      }
      if (h > 200) h = 0; // full-screen overlay, not a header — ignore it
      root.style.setProperty('--shows-header-h', h + 'px');
      var bar = root.querySelector('.acg-shows__bar');
      if (bar) root.style.setProperty('--shows-bar-h', bar.offsetHeight + 'px');
      if (map) map.invalidateSize();
    }

    syncHeaderOffset();
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(syncHeaderOffset, 150);
    });

    load();
  }

  ready(function () {
    document.querySelectorAll('[data-acg-car-shows]').forEach(init);
  });
})();
